import { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
    Box, Typography, Button, Stack, Chip, CircularProgress,
    Input, Select, Option, IconButton, Divider, Switch,
    Tabs, TabList, Tab, TabPanel,
    Modal, ModalDialog, ModalClose, Alert,
} from '@mui/joy';
import { motion } from 'framer-motion';

import ArrowBackRoundedIcon              from '@mui/icons-material/ArrowBackRounded';
import TuneRoundedIcon                   from '@mui/icons-material/TuneRounded';
import LocationOnRoundedIcon             from '@mui/icons-material/LocationOnRounded';
import AccountBalanceWalletRoundedIcon   from '@mui/icons-material/AccountBalanceWalletRounded';
import EmojiEventsRoundedIcon            from '@mui/icons-material/EmojiEventsRounded';
import AccessTimeRoundedIcon             from '@mui/icons-material/AccessTimeRounded';
import StarRoundedIcon                   from '@mui/icons-material/StarRounded';
import AddRoundedIcon                    from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon          from '@mui/icons-material/DeleteOutlineRounded';
import ContentCopyRoundedIcon            from '@mui/icons-material/ContentCopyRounded';
import SaveRoundedIcon                   from '@mui/icons-material/SaveRounded';
import WarningRoundedIcon                from '@mui/icons-material/WarningRounded';

import Header from '../../components/Header';
import { AppContext } from '../../context';
import {
    APIGetLocations,
    APIGetSalaryConfigs,
    APICreateSalaryConfig,
    APIUpdateSalaryConfig,
    APIDeleteSalaryConfig,
    parseApiError,
} from '../../api';
import MemeLoader from '../../components/MemeLoader';

// ─── Анимации ──────────────────────────────────────────────
const pageVariants = {
    initial: { opacity: 0, y: 8 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.4, 0, 0.2, 1] } },
    exit:    { opacity: 0, y: -8, transition: { duration: 0.2 } },
};

const stagger = {
    hidden:  { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
};

const fadeUp = {
    hidden:  { opacity: 0, y: 8 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] } },
};

// ─── Дефолтные структуры ────────────────────────────────────
const DEFAULT_TIERS = [
    { min: 0, max: 5000, percent: 20 },
];

const DEFAULT_REWARD = {
    threshold:   3000,
    base_reward: 200,
    per_step:    1000,
    step_reward: 100,
};

const DEFAULT_DAILY_REWARD = {
    threshold:   10000,
    base_reward: 400,
    per_step:    5000,
    step_reward: 200,
};

const DEFAULT_ADMIN_SALARY = {
    day_rate:        500,
    night_rate:      250,
    day_start:       '08:00',
    day_end:         '18:00',
    revenue_percent: 8,
};

const DEFAULT_ADMIN_REWARD = {
    threshold:   10000,
    base_reward: 300,
    per_step:    5000,
    step_reward: 200,
};

const DEFAULT_REWARD_BONUS = {
    per_step:    1000,
    step_reward: 100,
};

// Приводим «премию за премию» к валидным числам (пусто/NaN → дефолт),
// чтобы на сервер не ушёл null (иначе расчёт премии упадёт). 0 сохраняем как есть.
const num = (v, def) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : def;
};
const cleanBonus = (cfg) => ({
    per_step:    num(cfg?.per_step,    DEFAULT_REWARD_BONUS.per_step),
    step_reward: num(cfg?.step_reward, DEFAULT_REWARD_BONUS.step_reward),
});

// ─── Компонент: редактор зарплатных порогов ─────────────────
function TiersEditor({ tiers, onChange }) {
    const update = (idx, field, value) => {
        const next = tiers.map((t, i) => i === idx ? { ...t, [field]: value === '' ? null : parseFloat(value) } : t);
        onChange(next);
    };

    const remove = (idx) => onChange(tiers.filter((_, i) => i !== idx));

    const add = () => {
        const last = tiers[tiers.length - 1];
        const newMin = last?.max ?? 0;
        onChange([...tiers, { min: newMin, max: newMin + 5000, percent: 20 }]);
    };

    return (
        <Stack spacing={1}>
            {tiers.map((tier, idx) => (
                <Stack direction={'row'} key={idx} sx={{
                    display: 'flex', gap: 0.8, flexWrap: 'wrap', alignItems: 'center',
                    p: 1, borderRadius: 'var(--joy-radius-md)',
                    bgcolor: 'var(--joy-palette-background-level2)',
                    border: '1px solid var(--joy-palette-divider)',
                }}>
                    <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)', width: 32 }}>
                        От
                    </Typography>
                    <Input
                        type="number" size="sm"
                        value={tier.min ?? ''}
                        onChange={(e) => update(idx, 'min', e.target.value)}
                        endDecorator="₽"
                        sx={{ width: 100 }}
                    />
                    <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                        до
                    </Typography>
                    <Input
                        type="number" size="sm"
                        value={tier.max ?? ''}
                        onChange={(e) => update(idx, 'max', e.target.value)}
                        placeholder="∞"
                        endDecorator="₽"
                        sx={{ width: 100 }}
                    />
                    <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                        →
                    </Typography>
                    <Input
                        type="number" size="sm"
                        value={tier.percent ?? ''}
                        onChange={(e) => update(idx, 'percent', e.target.value)}
                        endDecorator="%"
                        sx={{ width: 80 }}
                    />
                    <IconButton
                        size="sm" variant="plain" color="danger"
                        onClick={() => remove(idx)}
                        disabled={tiers.length <= 1}
                    >
                        <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Stack>
            ))}
            <Button
                size="sm" variant="outlined" color="neutral"
                startDecorator={<AddRoundedIcon sx={{ fontSize: 14 }} />}
                onClick={add}
            >
                Добавить порог
            </Button>
            <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                Пустое «до» = без верхней границы (применяется ко всему выше).
            </Typography>
        </Stack>
    );
}

// ─── Компонент: редактор премии (threshold, base, step) ─────
function RewardConfigEditor({ value, onChange, hint }) {
    const set = (field) => (e) => {
        const v = e.target.value === '' ? null : parseFloat(e.target.value);
        onChange({ ...value, [field]: v });
    };

    return (
        <Stack spacing={1.5}>
            <Stack direction={'column'} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.2 }}>
                <Box>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', textTransform: 'uppercase', mb: 0.4 }}>
                        Порог
                    </Typography>
                    <Input
                        type="number" size="sm"
                        value={value.threshold ?? ''}
                        onChange={set('threshold')}
                        endDecorator="₽"
                    />
                </Box>
                <Box>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', textTransform: 'uppercase', mb: 0.4 }}>
                        Базовая премия
                    </Typography>
                    <Input
                        type="number" size="sm"
                        value={value.base_reward ?? ''}
                        onChange={set('base_reward')}
                        endDecorator="₽"
                    />
                </Box>
                <Box>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', textTransform: 'uppercase', mb: 0.4 }}>
                        Шаг
                    </Typography>
                    <Input
                        type="number" size="sm"
                        value={value.per_step ?? ''}
                        onChange={set('per_step')}
                        endDecorator="₽"
                    />
                </Box>
                <Box>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', textTransform: 'uppercase', mb: 0.4 }}>
                        Премия за шаг
                    </Typography>
                    <Input
                        type="number" size="sm"
                        value={value.step_reward ?? ''}
                        onChange={set('step_reward')}
                        endDecorator="₽"
                    />
                </Box>
            </Stack>
            {hint && (
                <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                    {hint}
                </Typography>
            )}
        </Stack>
    );
}

// ─── Компонент: редактор «премии за премию» (per_step, step_reward) ─────
function RewardBonusEditor({ value, onChange, hint }) {
    const set = (field) => (e) => {
        const v = e.target.value === '' ? null : parseFloat(e.target.value);
        onChange({ ...value, [field]: v });
    };

    return (
        <Stack spacing={1.5}>
            <Stack direction={'column'} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.2 }}>
                <Box>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', textTransform: 'uppercase', mb: 0.4 }}>
                        Шаг (за сколько ₽ премии)
                    </Typography>
                    <Input
                        type="number" size="sm"
                        value={value.per_step ?? ''}
                        onChange={set('per_step')}
                        placeholder="1000"
                        endDecorator="₽"
                    />
                </Box>
                <Box>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', textTransform: 'uppercase', mb: 0.4 }}>
                        Премия за шаг
                    </Typography>
                    <Input
                        type="number" size="sm"
                        value={value.step_reward ?? ''}
                        onChange={set('step_reward')}
                        placeholder="100"
                        endDecorator="₽"
                    />
                </Box>
            </Stack>
            {hint && (
                <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                    {hint}
                </Typography>
            )}
        </Stack>
    );
}

// ─── Компонент: редактор оклада админа ──────────────────────
function AdminSalaryEditor({ value, onChange }) {
    const set = (field) => (e) => {
        const v = e.target.value === '' ? null : (field.includes('rate') || field === 'revenue_percent' ? parseFloat(e.target.value) : e.target.value);
        onChange({ ...value, [field]: v });
    };

    return (
        <Stack spacing={1.5}>
            <Stack direction={'column'} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.2 }}>
                <Box>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', textTransform: 'uppercase', mb: 0.4 }}>
                        Ставка в день
                    </Typography>
                    <Input
                        type="number" size="sm"
                        value={value.day_rate ?? ''}
                        onChange={set('day_rate')}
                        endDecorator="₽/ч"
                    />
                </Box>
                <Box>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', textTransform: 'uppercase', mb: 0.4 }}>
                        Ставка ночью
                    </Typography>
                    <Input
                        type="number" size="sm"
                        value={value.night_rate ?? ''}
                        onChange={set('night_rate')}
                        endDecorator="₽/ч"
                    />
                </Box>
                <Box>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', textTransform: 'uppercase', mb: 0.4 }}>
                        Начало дня
                    </Typography>
                    <Input
                        type="time" size="sm"
                        value={value.day_start ?? '08:00'}
                        onChange={set('day_start')}
                    />
                </Box>
                <Box>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', textTransform: 'uppercase', mb: 0.4 }}>
                        Конец дня
                    </Typography>
                    <Input
                        type="time" size="sm"
                        value={value.day_end ?? '18:00'}
                        onChange={set('day_end')}
                    />
                </Box>
            </Stack>
            <Box>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', textTransform: 'uppercase', mb: 0.4 }}>
                    Процент от выручки точки
                </Typography>
                <Input
                    type="number" size="sm"
                    value={value.revenue_percent ?? ''}
                    onChange={set('revenue_percent')}
                    endDecorator="%"
                    sx={{ maxWidth: 200 }}
                />
            </Box>
        </Stack>
    );
}

// ─── Аккордеон-секция ───────────────────────────────────────
function Section({ icon, title, description, color, children, defaultOpen = false }) {
    const [open, setOpen] = useState(defaultOpen);

    return (
        <Box sx={{
            borderRadius: 'var(--joy-radius-lg)',
            border: '1px solid var(--joy-palette-divider)',
            bgcolor: 'var(--joy-palette-background-level1)',
            overflow: 'hidden',
        }}>
            <Box
                onClick={() => setOpen(v => !v)}
                sx={{
                    display: 'flex', alignItems: 'center', gap: 1.2,
                    px: 2, py: 1.5,
                    cursor: 'pointer',
                    transition: 'all .15s',
                    '&:hover': { bgcolor: 'var(--joy-palette-background-level2)' },
                    borderBottom: open ? '1px solid var(--joy-palette-divider)' : 'none',
                }}
            >
                <Box sx={{
                    width: 32, height: 32, borderRadius: 'var(--joy-radius-md)',
                    bgcolor: `${color}20`, color,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    {icon}
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                        {title}
                    </Typography>
                    {description && (
                        <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                            {description}
                        </Typography>
                    )}
                </Box>
                <Typography sx={{ fontSize: 18, color: 'var(--joy-palette-text-tertiary)' }}>
                    {open ? '−' : '+'}
                </Typography>
            </Box>
            {open && (
                <Box sx={{ p: 2 }}>
                    {children}
                </Box>
            )}
        </Box>
    );
}

// ─── Карточка конфигурации в боковой панели ─────────────────
function ConfigCard({ config, active, onClick, dirty }) {
    const isDefault = !config.location;

    return (
        <Box
            onClick={onClick}
            sx={{
                p: 1.5,
                borderRadius: 'var(--joy-radius-md)',
                border: '1.5px solid',
                borderColor: active ? 'var(--joy-palette-primary-500)' : 'var(--joy-palette-divider)',
                bgcolor: active ? 'rgba(162,38,226,0.08)' : 'var(--joy-palette-background-level1)',
                cursor: 'pointer',
                transition: 'all .15s',
                '&:hover': { borderColor: active ? 'var(--joy-palette-primary-500)' : 'rgba(162,38,226,0.3)' },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.4 }}>
                {isDefault
                    ? <StarRoundedIcon sx={{ fontSize: 14, color: '#f59e0b' }} />
                    : <LocationOnRoundedIcon sx={{ fontSize: 14, color: active ? 'var(--joy-palette-primary-300)' : 'var(--joy-palette-text-tertiary)' }} />
                }
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                    {config.location_name || 'По умолчанию'}
                </Typography>
                {dirty && (
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#f59e0b', ml: 'auto' }} />
                )}
            </Box>
            {config.name && (
                <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                    {config.name}
                </Typography>
            )}
        </Box>
    );
}

// ══════════════════════════════════════════════════════════════════
//  SalaryConfigPage
// ══════════════════════════════════════════════════════════════════
export default function SalaryConfigPage() {
    const navigate = useNavigate();
    const { token, hasPerm, showToast, triggerRefresh } = useContext(AppContext);

    const canEdit   = hasPerm('finance.configs.update');
    const canCreate = hasPerm('finance.configs.create');
    const canDelete = hasPerm('finance.configs.delete');

    const [configs,    setConfigs]    = useState([]);
    const [locations,  setLocations]  = useState([]);
    const [activeId,   setActiveId]   = useState(null);
    const [loading,    setLoading]    = useState(true);
    const [saving,     setSaving]     = useState(false);

    // Локальное состояние формы для активного конфига
    const [form,       setForm]       = useState(null);
    const [initialForm, setInitialForm] = useState(null);

    // Модалка создания
    const [createModal, setCreateModal] = useState({ open: false, locationId: '', copyFromId: '' });

    // Модалка удаления
    const [deleteModal, setDeleteModal] = useState({ open: false, config: null });

    // ── Загрузка ──────────────────────────────────────────────
    const loadAll = useCallback(async () => {
        setLoading(true);
        const [cfgs, locs] = await Promise.all([
            APIGetSalaryConfigs(token),
            APIGetLocations(token),
        ]);

        if (Array.isArray(cfgs))  setConfigs(cfgs);
        if (Array.isArray(locs))  setLocations(locs);

        // Выбираем активный конфиг — первый, или текущий если ещё валиден
        if (Array.isArray(cfgs) && cfgs.length > 0) {
            const stillExists = activeId && cfgs.find(c => c.id === activeId);
            const target = stillExists || cfgs[0];
            setActiveId(target.id);
            setForm(cloneForm(target));
            setInitialForm(cloneForm(target));
        } else {
            setActiveId(null);
            setForm(null);
            setInitialForm(null);
        }
        setLoading(false);
    }, [token, activeId]);

    useEffect(() => { loadAll(); }, []); // eslint-disable-line

    // ── Переключение между конфигами ─────────────────────────
    const handleSelect = (cfg) => {
        // Если есть несохранённые изменения — предупреждаем
        if (form && initialForm && !isFormEqual(form, initialForm)) {
            if (!window.confirm('Несохранённые изменения будут потеряны. Продолжить?')) return;
        }
        setActiveId(cfg.id);
        setForm(cloneForm(cfg));
        setInitialForm(cloneForm(cfg));
    };

    // ── Сохранение ───────────────────────────────────────────
    const handleSave = async () => {
        if (!form || !activeId) return;

        setSaving(true);
        const payload = {
            name:                 form.name || '',
            salary_tiers:         form.salary_tiers || [],
            reward_config:        form.reward_config || {},
            daily_reward_config:  form.daily_reward_config || {},
            admin_salary_config:  form.admin_salary_config || {},
            admin_reward_config:  form.admin_reward_config || {},
            reward_bonus_config:  cleanBonus(form.reward_bonus_config),
            reward_bonus_enabled: form.reward_bonus_enabled !== false,
            has_administrators:   !!form.has_administrators,
        };

        const res = await APIUpdateSalaryConfig(token, activeId, payload);
        setSaving(false);

        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось сохранить'), 'danger');
            return;
        }

        showToast('Конфигурация сохранена ✓');
        setForm(cloneForm(res));
        setInitialForm(cloneForm(res));
        setConfigs(prev => prev.map(c => c.id === res.id ? res : c));
        if (typeof triggerRefresh === 'function') triggerRefresh();
    };

    // ── Создание новой ───────────────────────────────────────
    const handleCreate = async () => {
        setSaving(true);
        const locationId = createModal.locationId === '' ? null : parseInt(createModal.locationId);
        const copyFrom   = createModal.copyFromId ? configs.find(c => c.id === parseInt(createModal.copyFromId)) : null;

        const payload = {
            location: locationId,
            name: '',
            salary_tiers:        copyFrom?.salary_tiers        || DEFAULT_TIERS,
            reward_config:       copyFrom?.reward_config       || DEFAULT_REWARD,
            daily_reward_config: copyFrom?.daily_reward_config || DEFAULT_DAILY_REWARD,
            admin_salary_config: copyFrom?.admin_salary_config || DEFAULT_ADMIN_SALARY,
            admin_reward_config: copyFrom?.admin_reward_config || DEFAULT_ADMIN_REWARD,
            reward_bonus_config: cleanBonus(copyFrom?.reward_bonus_config || DEFAULT_REWARD_BONUS),
            reward_bonus_enabled: copyFrom?.reward_bonus_enabled ?? true,
            has_administrators:  copyFrom?.has_administrators ?? false,
        };

        const res = await APICreateSalaryConfig(token, payload);
        setSaving(false);

        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось создать'), 'danger');
            return;
        }

        showToast('Конфигурация создана ✓');
        setCreateModal({ open: false, locationId: '', copyFromId: '' });
        setConfigs(prev => [...prev, res]);
        setActiveId(res.id);
        setForm(cloneForm(res));
        setInitialForm(cloneForm(res));
    };

    // ── Удаление ─────────────────────────────────────────────
    const handleDelete = async () => {
        if (!deleteModal.config) return;
        setSaving(true);
        const res = await APIDeleteSalaryConfig(token, deleteModal.config.id);
        setSaving(false);

        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось удалить'), 'danger');
            return;
        }

        showToast('Конфигурация удалена');
        setDeleteModal({ open: false, config: null });
        setConfigs(prev => prev.filter(c => c.id !== deleteModal.config.id));

        if (activeId === deleteModal.config.id) {
            const remaining = configs.filter(c => c.id !== deleteModal.config.id);
            if (remaining.length > 0) {
                handleSelect(remaining[0]);
            } else {
                setActiveId(null);
                setForm(null);
                setInitialForm(null);
            }
        }
    };

    // ── Доступные точки для создания (без существующего конфига) ──
    const availableLocations = useMemo(() => {
        const taken = new Set(configs.map(c => c.location).filter(Boolean));
        const hasDefault = configs.some(c => !c.location);
        return [
            ...(!hasDefault ? [{ id: '', name: '⭐ По умолчанию (для всех точек без своей)' }] : []),
            ...locations.filter(l => !taken.has(l.id)),
        ];
    }, [configs, locations]);

    const isDirty = form && initialForm && !isFormEqual(form, initialForm);
    const activeConfig = configs.find(c => c.id === activeId);

    // ── Рендер ────────────────────────────────────────────────
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: 'var(--joy-palette-background-body)' }}>
                <MemeLoader />
            </Box>
        );
    }

    return (
        <motion.div
            variants={pageVariants} initial="initial" animate="animate" exit="exit"
            style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}
        >
            <Header />

            <Box sx={{
                maxWidth: 1100,
                mx: 'auto',
                px: { xs: 2.5, sm: 3 },
                py: 3,
                pb: { xs: 12, sm: 3 },
            }}>
                {/* ── Шапка ───────────────────────────────────── */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: 3,
                    pt: { xs: 1, sm: 0 },
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <IconButton variant="plain" onClick={() => navigate('/manager')}
                            sx={{ color: 'var(--joy-palette-text-secondary)' }}>
                            <ArrowBackRoundedIcon />
                        </IconButton>
                        <Box>
                            <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--joy-palette-text-primary)' }}>
                                Условия расчёта
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mt: 0.3 }}>
                                Автоматика премий, % фотографа, оклады администраторов
                            </Typography>
                        </Box>
                    </Box>

                    {canCreate && availableLocations.length > 0 && (
                        <Button
                            startDecorator={<AddRoundedIcon sx={{ fontSize: 16 }} />}
                            variant="soft" color="primary"
                            onClick={() => setCreateModal({ open: true, locationId: '', copyFromId: '' })}
                        >
                            Новая конфигурация
                        </Button>
                    )}
                </Box>

                {configs.length === 0 ? (
                    <Box sx={{
                        textAlign: 'center', py: 8,
                        borderRadius: 'var(--joy-radius-xl)',
                        border: '1px dashed var(--joy-palette-divider)',
                        bgcolor: 'var(--joy-palette-background-level1)',
                    }}>
                        <TuneRoundedIcon sx={{ fontSize: 48, color: 'var(--joy-palette-text-tertiary)', mb: 1 }} />
                        <Typography sx={{ fontSize: 14, color: 'var(--joy-palette-text-secondary)', mb: 0.5 }}>
                            Нет ни одной конфигурации
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', mb: 2 }}>
                            Создайте первую — она будет применяться ко всем точкам без собственной конфигурации.
                        </Typography>
                        {canCreate && (
                            <Button
                                variant="solid" color="primary"
                                onClick={() => setCreateModal({ open: true, locationId: '', copyFromId: '' })}
                                startDecorator={<AddRoundedIcon sx={{ fontSize: 16 }} />}
                            >
                                Создать конфигурацию
                            </Button>
                        )}
                    </Box>
                ) : (
                    <Box sx={{ display: { xs: 'flex', sm: 'grid' }, flexDirection: 'column', gridTemplateColumns: { xs: '1fr', md: '280px 1fr' }, gap: 2.5 }}>

                        {/* ── Левая панель: список конфигов ────── */}
                        <Box>
                            <Typography sx={{
                                fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
                                textTransform: 'uppercase',
                                color: 'var(--joy-palette-text-secondary)',
                                mb: 1,
                            }}>
                                Конфигурации ({configs.length})
                            </Typography>
                            <Stack spacing={1}>
                                {configs.map(c => (
                                    <ConfigCard
                                        key={c.id}
                                        config={c}
                                        active={c.id === activeId}
                                        onClick={() => handleSelect(c)}
                                        dirty={c.id === activeId && isDirty}
                                    />
                                ))}
                            </Stack>
                        </Box>

                        {/* ── Правая панель: редактор активной конфигурации ── */}
                        <Box>
                            {form && activeConfig && (
                                <motion.div variants={stagger} initial="hidden" animate="visible">
                                    <Stack spacing={2}>

                                        {/* Шапка редактора */}
                                        <motion.div variants={fadeUp}>
                                            <Box sx={{
                                                p: 2,
                                                borderRadius: 'var(--joy-radius-lg)',
                                                border: '1px solid var(--joy-palette-divider)',
                                                bgcolor: 'var(--joy-palette-background-level1)',
                                            }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
                                                    <Box>
                                                        <Typography sx={{ fontSize: 16, fontWeight: 800, color: 'var(--joy-palette-text-primary)' }}>
                                                            {activeConfig.location_name || 'По умолчанию'}
                                                        </Typography>
                                                        {!activeConfig.location && (
                                                            <Typography sx={{ fontSize: 11, color: '#f59e0b', mt: 0.2 }}>
                                                                ⭐ Применяется ко всем точкам без своей конфигурации
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    {canDelete && (
                                                        <IconButton
                                                            size="sm" variant="soft" color="danger"
                                                            onClick={() => setDeleteModal({ open: true, config: activeConfig })}
                                                        >
                                                            <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                                <Box>
                                                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', textTransform: 'uppercase', mb: 0.4 }}>
                                                        Название (для удобства)
                                                    </Typography>
                                                    <Input
                                                        size="sm"
                                                        value={form.name || ''}
                                                        onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                                                        placeholder="Например: Уличный · Ресторанный · Парк"
                                                        disabled={!canEdit}
                                                    />
                                                </Box>

                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                                                    <Switch
                                                        size="sm"
                                                        checked={!!form.has_administrators}
                                                        onChange={(e) => setForm(prev => ({ ...prev, has_administrators: e.target.checked }))}
                                                        disabled={!canEdit}
                                                    />
                                                    <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)' }}>
                                                        На этой точке работают администраторы
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </motion.div>

                                        {/* Зарплата фотографа */}
                                        <motion.div variants={fadeUp}>
                                            <Section
                                                icon={<AccountBalanceWalletRoundedIcon sx={{ fontSize: 16 }} />}
                                                title="Зарплата фотографа"
                                                description="Пороги % от выручки за день"
                                                color="#a226e2"
                                                defaultOpen
                                            >
                                                <TiersEditor
                                                    tiers={form.salary_tiers || DEFAULT_TIERS}
                                                    onChange={(t) => setForm(prev => ({ ...prev, salary_tiers: t }))}
                                                />
                                            </Section>
                                        </motion.div>

                                        {/* Премия за чек */}
                                        <motion.div variants={fadeUp}>
                                            <Section
                                                icon={<EmojiEventsRoundedIcon sx={{ fontSize: 16 }} />}
                                                title="Премия за чек"
                                                description="Когда чек больше порога — начисляем премию"
                                                color="#22c55e"
                                            >
                                                <RewardConfigEditor
                                                    value={form.reward_config || DEFAULT_REWARD}
                                                    onChange={(v) => setForm(prev => ({ ...prev, reward_config: v }))}
                                                    hint="Пример: порог 3000, базовая 200₽; за каждые +1000 ещё 100₽."
                                                />
                                            </Section>
                                        </motion.div>

                                        {/* Ежедневная премия */}
                                        <motion.div variants={fadeUp}>
                                            <Section
                                                icon={<StarRoundedIcon sx={{ fontSize: 16 }} />}
                                                title="Ежедневная премия"
                                                description="Премия от суммарной выручки за день"
                                                color="#3b82f6"
                                            >
                                                <RewardConfigEditor
                                                    value={form.daily_reward_config || DEFAULT_DAILY_REWARD}
                                                    onChange={(v) => setForm(prev => ({ ...prev, daily_reward_config: v }))}
                                                    hint="Пример: порог 10000₽ в день — премия 400₽; +5000 — ещё 200₽."
                                                />
                                            </Section>
                                        </motion.div>

                                        {/* Премия за премию */}
                                        <motion.div variants={fadeUp}>
                                            <Section
                                                icon={<EmojiEventsRoundedIcon sx={{ fontSize: 16 }} />}
                                                title="Премия за премию"
                                                description="Бонус за суммарную дневную премию"
                                                color="#a855f7"
                                            >
                                                <Stack spacing={1.5}>
                                                    <Box sx={{
                                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                        gap: 1, p: 1, borderRadius: 'var(--joy-radius-md)',
                                                        bgcolor: 'var(--joy-palette-background-level2)',
                                                        border: '1px solid var(--joy-palette-divider)',
                                                    }}>
                                                        <Box sx={{ pr: 1 }}>
                                                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                                Начислять премию за премию
                                                            </Typography>
                                                            <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                                                                Выключите, чтобы на этой точке бонус не начислялся.
                                                            </Typography>
                                                        </Box>
                                                        <Switch
                                                            size="sm"
                                                            checked={form.reward_bonus_enabled !== false}
                                                            onChange={(e) => setForm(prev => ({ ...prev, reward_bonus_enabled: e.target.checked }))}
                                                            disabled={!canEdit}
                                                        />
                                                    </Box>

                                                    {form.reward_bonus_enabled !== false && (
                                                        <RewardBonusEditor
                                                            value={form.reward_bonus_config || DEFAULT_REWARD_BONUS}
                                                            onChange={(v) => setForm(prev => ({ ...prev, reward_bonus_config: v }))}
                                                            hint="За каждые +1000₽ суммарной дневной премии — ещё +100₽."
                                                        />
                                                    )}
                                                </Stack>
                                            </Section>
                                        </motion.div>

                                        {/* Оклад админа (только если has_administrators) */}
                                        {form.has_administrators && (
                                            <>
                                                <motion.div variants={fadeUp}>
                                                    <Section
                                                        icon={<AccessTimeRoundedIcon sx={{ fontSize: 16 }} />}
                                                        title="Оклад администратора"
                                                        description="Часовые ставки + % от выручки точки"
                                                        color="#06b6d4"
                                                    >
                                                        <AdminSalaryEditor
                                                            value={form.admin_salary_config || DEFAULT_ADMIN_SALARY}
                                                            onChange={(v) => setForm(prev => ({ ...prev, admin_salary_config: v }))}
                                                        />
                                                    </Section>
                                                </motion.div>

                                                <motion.div variants={fadeUp}>
                                                    <Section
                                                        icon={<EmojiEventsRoundedIcon sx={{ fontSize: 16 }} />}
                                                        title="Премия администратора"
                                                        description="За каждый чек ≥ порога"
                                                        color="#f59e0b"
                                                    >
                                                        <RewardConfigEditor
                                                            value={form.admin_reward_config || DEFAULT_ADMIN_REWARD}
                                                            onChange={(v) => setForm(prev => ({ ...prev, admin_reward_config: v }))}
                                                        />
                                                    </Section>
                                                </motion.div>
                                            </>
                                        )}

                                        {/* Fixed-кнопка сохранения внизу экрана */}
                                        {canEdit && isDirty && (
                                            <Box sx={{
                                                position: 'fixed',
                                                bottom: 0, left: 0, right: 0,
                                                zIndex: 20,
                                                bgcolor: 'rgba(20,15,40,0.92)',
                                                backdropFilter: 'blur(10px)',
                                                WebkitBackdropFilter: 'blur(10px)',
                                                borderTop: '1px solid var(--joy-palette-divider)',
                                                px: 2, py: 1.5,
                                                display: 'flex',
                                                justifyContent: 'center',
                                            }}>
                                                <Box sx={{
                                                    display: 'flex', justifyContent: 'flex-end', gap: 1,
                                                    width: '100%', maxWidth: 1100,
                                                }}>
                                                    <Button
                                                        variant="outlined" color="neutral"
                                                        onClick={() => setForm(cloneForm(initialForm))}
                                                    >
                                                        Отмена
                                                    </Button>
                                                    <Button
                                                        variant="solid" color="primary"
                                                        onClick={handleSave}
                                                        loading={saving}
                                                        startDecorator={<SaveRoundedIcon sx={{ fontSize: 16 }} />}
                                                    >
                                                        Сохранить изменения
                                                    </Button>
                                                </Box>
                                            </Box>
                                        )}
                                    </Stack>
                                </motion.div>
                            )}
                        </Box>
                    </Box>
                )}
            </Box>

            {/* ── Модалка создания ─────────────────────────────── */}
            <Modal
                open={createModal.open}
                onClose={() => !saving && setCreateModal({ open: false, locationId: '', copyFromId: '' })}
                slotProps={{ backdrop: { sx: { backdropFilter: 'blur(6px)', bgcolor: 'rgba(0,0,0,0.65)' } } }}
            >
                <ModalDialog sx={{
                    bgcolor: 'var(--joy-palette-background-level1)',
                    border: '1px solid var(--joy-palette-divider)',
                    borderRadius: 'var(--joy-radius-xl)',
                    maxWidth: 440, width: '100%', p: 3,
                    maxHeight: { xs: '100dvh', sm: '90vh' },
                    height:    { xs: '100dvh', sm: 'auto' },
                    overflowY: 'auto',
                }}>
                    <ModalClose />
                    <Typography sx={{ fontSize: 18, fontWeight: 800, mb: 2 }}>
                        Новая конфигурация расчёта
                    </Typography>

                    <Stack spacing={1.5}>
                        <Box>
                            <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', textTransform: 'uppercase', mb: 0.4 }}>
                                Применять к точке *
                            </Typography>
                            <Select
                                value={createModal.locationId}
                                onChange={(_, val) => setCreateModal(prev => ({ ...prev, locationId: val ?? '' }))}
                                placeholder="Выберите точку"
                            >
                                {availableLocations.map(l => (
                                    <Option key={l.id || 'default'} value={String(l.id)}>
                                        {l.name}
                                    </Option>
                                ))}
                            </Select>
                        </Box>

                        {configs.length > 0 && (
                            <Box>
                                <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', textTransform: 'uppercase', mb: 0.4 }}>
                                    Скопировать с конфигурации
                                </Typography>
                                <Select
                                    value={createModal.copyFromId}
                                    onChange={(_, val) => setCreateModal(prev => ({ ...prev, copyFromId: val ?? '' }))}
                                    placeholder="Не копировать (дефолтные значения)"
                                >
                                    <Option value="">Не копировать</Option>
                                    {configs.map(c => (
                                        <Option key={c.id} value={String(c.id)}>
                                            {c.location_name}{c.name ? ` — ${c.name}` : ''}
                                        </Option>
                                    ))}
                                </Select>
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <Button
                                variant="outlined" color="neutral"
                                onClick={() => setCreateModal({ open: false, locationId: '', copyFromId: '' })}
                                disabled={saving}
                            >
                                Отмена
                            </Button>
                            <Button
                                variant="solid" color="primary"
                                onClick={handleCreate}
                                loading={saving}
                            >
                                Создать
                            </Button>
                        </Box>
                    </Stack>
                </ModalDialog>
            </Modal>

            {/* ── Модалка удаления ─────────────────────────────── */}
            <Modal
                open={deleteModal.open}
                onClose={() => !saving && setDeleteModal({ open: false, config: null })}
                slotProps={{ backdrop: { sx: { backdropFilter: 'blur(6px)', bgcolor: 'rgba(0,0,0,0.65)' } } }}
            >
                <ModalDialog sx={{
                    bgcolor: 'var(--joy-palette-background-level1)',
                    border: '1px solid var(--joy-palette-divider)',
                    borderRadius: 'var(--joy-radius-xl)',
                    maxWidth: 400, width: '100%', p: 3,
                    maxHeight: 'calc(100dvh - 32px)', overflowY: 'auto',
                }}>
                    <ModalClose />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <WarningRoundedIcon sx={{ fontSize: 20, color: '#f87171' }} />
                        <Typography sx={{ fontSize: 16, fontWeight: 800 }}>
                            Удалить конфигурацию?
                        </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mb: 2 }}>
                        Точка «<b>{deleteModal.config?.location_name}</b>» начнёт использовать
                        дефолтную конфигурацию. Уже начисленные премии останутся,
                        но новые будут считаться по другим правилам.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant="outlined" color="neutral"
                            onClick={() => setDeleteModal({ open: false, config: null })}
                            disabled={saving}
                        >
                            Отмена
                        </Button>
                        <Button variant="solid" color="danger"
                            onClick={handleDelete}
                            loading={saving}
                        >
                            Удалить
                        </Button>
                    </Box>
                </ModalDialog>
            </Modal>
        </motion.div>
    );
}

// ─── Утилиты ─────────────────────────────────────────────────
function cloneForm(cfg) {
    if (!cfg) return null;
    return {
        id:                  cfg.id,
        name:                cfg.name || '',
        salary_tiers:        Array.isArray(cfg.salary_tiers) ? JSON.parse(JSON.stringify(cfg.salary_tiers)) : [],
        reward_config:       cfg.reward_config       ? { ...cfg.reward_config }       : {},
        daily_reward_config: cfg.daily_reward_config ? { ...cfg.daily_reward_config } : {},
        admin_salary_config: cfg.admin_salary_config ? { ...cfg.admin_salary_config } : {},
        admin_reward_config: cfg.admin_reward_config ? { ...cfg.admin_reward_config } : {},
        reward_bonus_config: cfg.reward_bonus_config ? { ...cfg.reward_bonus_config } : {},
        reward_bonus_enabled: cfg.reward_bonus_enabled ?? true,
        has_administrators:  !!cfg.has_administrators,
    };
}

function isFormEqual(a, b) {
    return JSON.stringify(a) === JSON.stringify(b);
}